import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ChevronRight, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  MAX_ACTIVE_PROMOTIONAL_ADS_PER_STORE,
  validateExternalPromotionalAdUrl,
  useTranslation,
  type AdminStorePromotionalAdDestination,
  type PromotionalAdErrorKey,
  type StorePromotionalAdDestination,
  type StorePromotionalAdWithPreview,
  type TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useNavBlocker, useNavBlockerMarkSettled } from '@/hooks/use-nav-blocker';
import {
  createPromotionalAdFormSchema,
  type PromotionalAdFormValues,
} from '../lib/promotional-ad-form-schema';
import { PromotionalAdDestinationSection } from './promotional-ad-destination-section';
import { PromotionalAdMediaPicker } from './promotional-ad-media-picker';

export interface PromotionalAdFormSubmit {
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly destination: StorePromotionalAdDestination;
  readonly destinationChanged: boolean;
  readonly is_active: boolean;
  readonly image: File | null;
}

export interface PromotionalAdFormPageProps {
  readonly mode: 'create' | 'edit';
  readonly storeId: string | null;
  readonly storeSlug: string;
  readonly ad?: StorePromotionalAdWithPreview | null;
  readonly activeAdCount?: number;
  readonly isSubmitting?: boolean;
  readonly errorKey?: PromotionalAdErrorKey | null;
  readonly onSubmit: (values: PromotionalAdFormSubmit) => Promise<void>;
  readonly onCancel: () => void;
}

const EMPTY_DESTINATION: PromotionalAdFormValues['destination'] = { type: 'none' };

function toFormDestination(
  destination: AdminStorePromotionalAdDestination | null | undefined,
): PromotionalAdFormValues['destination'] {
  switch (destination?.type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return { type: 'category', categoryId: destination.categoryId };
    case 'product':
      return { type: 'product', productId: destination.productId };
    case 'external':
      return { type: 'external', url: destination.url };
    default:
      return EMPTY_DESTINATION;
  }
}

function toSubmitDestination(
  destination: PromotionalAdFormValues['destination'],
): StorePromotionalAdDestination {
  switch (destination.type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return { type: 'category', categoryId: destination.categoryId.trim() };
    case 'product':
      return { type: 'product', productId: destination.productId.trim() };
    case 'external': {
      const url = validateExternalPromotionalAdUrl(destination.url);
      return url ? { type: 'external', url } : { type: 'none' };
    }
    default:
      return { type: 'none' };
  }
}

function destinationErrorKey(
  errors: Partial<Record<string, unknown>>,
  destination: PromotionalAdFormValues['destination'],
): TranslationKey | null {
  const root = errors.destination as
    | {
        readonly message?: unknown;
        readonly categoryId?: { readonly message?: unknown };
        readonly productId?: { readonly message?: unknown };
        readonly url?: { readonly message?: unknown };
      }
    | undefined;
  const message =
    destination.type === 'category'
      ? root?.categoryId?.message
      : destination.type === 'product'
        ? root?.productId?.message
        : destination.type === 'external'
          ? root?.url?.message
          : root?.message;
  return typeof message === 'string' ? (message as TranslationKey) : null;
}

export function PromotionalAdFormPage({
  mode,
  storeId,
  storeSlug,
  ad,
  activeAdCount = 0,
  isSubmitting = false,
  errorKey,
  onSubmit,
  onCancel,
}: PromotionalAdFormPageProps) {
  const { t, dir } = useTranslation();
  const isEdit = mode === 'edit';
  const schema = useMemo(
    () => createPromotionalAdFormSchema({ requireImage: !isEdit, storeSlug }),
    [isEdit, storeSlug],
  );
  const defaultValues = useMemo<PromotionalAdFormValues>(
    () => ({
      title: ad?.title ?? '',
      alt_text: ad?.alt_text ?? '',
      destination: toFormDestination(ad?.destination),
      is_active: ad?.is_active ?? true,
      display_order: ad?.display_order ?? 0,
      image: null,
    }),
    [ad],
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<PromotionalAdFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues,
  });

  const destination = watch('destination');
  const markSettled = useNavBlockerMarkSettled();
  useNavBlocker(isDirty);

  const tooManyActiveAds =
    mode === 'create' && activeAdCount >= MAX_ACTIVE_PROMOTIONAL_ADS_PER_STORE;
  const submitLabelKey: TranslationKey =
    mode === 'create'
      ? 'admin.promotionalAds.form.submitCreate'
      : 'admin.promotionalAds.form.submitEdit';

  const submit = handleSubmit(async (values) => {
    if (tooManyActiveAds && values.is_active) return;
    const title = values.title.trim();
    const altText = values.alt_text.trim();
    markSettled();
    await onSubmit({
      title: title.length > 0 ? title : null,
      alt_text: altText.length > 0 ? altText : null,
      destination: toSubmitDestination(values.destination),
      destinationChanged: mode === 'create' || Boolean(dirtyFields.destination),
      is_active: values.is_active,
      image: values.image ?? null,
    });
  });

  const destinationFieldError = destinationErrorKey(errors, destination);

  return (
    <form
      noValidate
      dir={dir}
      data-testid="promotional-ad-form-page"
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      aria-busy={isSubmitting}
      onSubmit={submit}
    >
      <Link
        to="/promotions"
        className="text-zaatar mb-6 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
        data-testid="promotional-ad-form-back"
      >
        <ChevronRight size={16} className="rtl:rotate-180" aria-hidden strokeWidth={2} />
        {t('admin.promotionalAds.form.back' as TranslationKey)}
      </Link>

      <h1
        className="font-display text-foreground mb-8 text-start text-3xl tracking-tight"
        data-testid="promotional-ad-form-title"
      >
        {t(
          mode === 'create'
            ? ('admin.promotionalAds.form.pageTitleCreate' as TranslationKey)
            : ('admin.promotionalAds.form.pageTitleEdit' as TranslationKey),
        )}
      </h1>

      <div className="border-border bg-card shadow-admin-card space-y-6 rounded-lg border p-6 sm:p-8">
        {errorKey ? (
          <div
            role="alert"
            data-testid="promotional-ad-form-submit-error"
            className="bg-destructive/10 border-destructive/25 text-destructive flex items-start gap-2 rounded-md border px-4 py-3 text-start text-sm"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
            <span>{t(errorKey as TranslationKey)}</span>
          </div>
        ) : null}

        <Controller
          control={control}
          name="image"
          render={({ field }) => (
            <PromotionalAdMediaPicker
              file={field.value ?? null}
              existingUrl={ad?.preview_url ?? null}
              errorKey={(errors.image?.message as TranslationKey | undefined) ?? null}
              disabled={isSubmitting}
              required={!isEdit}
              onFileChange={(file) =>
                setValue('image', file, { shouldDirty: true, shouldValidate: true })
              }
            />
          )}
        />

        {tooManyActiveAds ? (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-start text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100"
          >
            {t('admin.promotionalAds.errors.tooManyActiveAds' as TranslationKey)}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="promotional-ad-title" className="mb-1.5 block text-start">
              {t('admin.promotionalAds.form.titleLabel' as TranslationKey)}
            </Label>
            <Input
              id="promotional-ad-title"
              type="text"
              dir="auto"
              maxLength={80}
              autoComplete="off"
              disabled={isSubmitting}
              placeholder={t('admin.promotionalAds.form.titlePlaceholder' as TranslationKey)}
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'promotional-ad-title-error' : undefined}
              data-testid="promotional-ad-title-input"
              {...register('title')}
            />
            {errors.title ? (
              <p
                id="promotional-ad-title-error"
                role="alert"
                className="text-destructive mt-1 text-start text-xs"
              >
                {t(errors.title.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <label className="border-border bg-admin-surface-muted flex items-start gap-3 rounded-lg border p-3 text-start">
            <input
              type="checkbox"
              className="accent-admin-lime mt-1 size-4"
              disabled={isSubmitting}
              data-testid="promotional-ad-active-checkbox"
              {...register('is_active')}
            />
            <span className="text-foreground block text-sm font-medium">
              {t('admin.promotionalAds.form.activeLabel' as TranslationKey)}
            </span>
          </label>
        </div>

        <div>
          <Label htmlFor="promotional-ad-alt" className="mb-1.5 block text-start">
            {t('admin.promotionalAds.form.altLabel' as TranslationKey)}
          </Label>
          <Textarea
            id="promotional-ad-alt"
            dir="auto"
            maxLength={160}
            disabled={isSubmitting}
            placeholder={t('admin.promotionalAds.form.altPlaceholder' as TranslationKey)}
            aria-invalid={!!errors.alt_text}
            aria-describedby={errors.alt_text ? 'promotional-ad-alt-error' : undefined}
            data-testid="promotional-ad-alt-input"
            {...register('alt_text')}
          />
          {errors.alt_text ? (
            <p
              id="promotional-ad-alt-error"
              role="alert"
              className="text-destructive mt-1 text-start text-xs"
            >
              {t(errors.alt_text.message as TranslationKey)}
            </p>
          ) : null}
        </div>

        <Controller
          control={control}
          name="destination"
          render={({ field }) => (
            <PromotionalAdDestinationSection
              storeId={storeId}
              value={field.value}
              initialDestination={ad?.destination ?? null}
              disabled={isSubmitting}
              errorKey={destinationFieldError}
              onChange={(next) => field.onChange(next)}
            />
          )}
        />
      </div>

      <div className="bg-background/95 border-border supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-20 border-t px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-screen-md flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" disabled={isSubmitting} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t(submitLabelKey)}</span>
          </Button>
        </div>
      </div>
    </form>
  );
}
